import { useMemo } from "react";
import { useSearchParams } from "react-router";

import {
  EmptystateOutputsColored,
  EmptystateSearchNoResultsColored,
} from "components/icons/generated";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import { URL_SEARCH_KEY } from "constants/urlQueryKeys";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import { useInitialSort } from "ds/components/Table/hooks/useInitialSort";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import { fuzzySearch } from "utils/fuzzySearch";

import { FILTERS_ORDER_SETTINGS_KEY_VERSION_OUTPUTS } from "./constants";
import { MODULE_VERSION_OUTPUTS_TABLE_CELLS } from "./Table/Cells";
import {
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  MODULE_VERSION_OUTPUTS_TABLE_FILTERS_STORAGE_KEY,
  SORT_CONFIG,
} from "./Table/constants";
import { useSortingModuleVersionOutputs } from "./Table/useSortingModuleVersionOutputs";
import useModuleVersionOutputsTableStore from "./Table/useTableStore";
import ModuleVersionPageIntoTitle from "../components/PageIntoTitle";
import ModuleVersionScrollableWrapper from "../components/ScrollableWrapper";

type ModuleVersionOutputsProps = {
  outputs: Array<{
    name: string;
    description?: string;
  }>;
};

const ModuleVersionOutputs = ({ outputs }: ModuleVersionOutputsProps) => {
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get(URL_SEARCH_KEY);
  const trackAnalytics = useAnalytics();

  useInitialSort(INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION);

  const filteredOutputs = useMemo(() => {
    const rows = outputs.map((output) => ({
      id: output.name,
      description: output.description,
    }));

    if (searchQuery) {
      return fuzzySearch(rows, searchQuery.trim(), {
        keys: ["id", "description"],
        scoreThreshold: -1000,
      });
    }

    return rows;
  }, [outputs, searchQuery]);

  const sortedOutputs = useSortingModuleVersionOutputs(filteredOutputs);

  return (
    <>
      <PageInfoHeaderWrapper>
        <ModuleVersionPageIntoTitle
          title="Outputs"
          tooltip="Outputs declared by the module. Can be used as inputs to other modules."
        />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder={SEARCH_PLACEHOLDERS.OUTPUTS}
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_VERSION_OUTPUTS}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ModuleVersionScrollableWrapper padding="0">
        {sortedOutputs.length === 0 && (
          <EmptyState
            padding="x-large"
            icon={searchQuery ? EmptystateSearchNoResultsColored : EmptystateOutputsColored}
            caption={
              searchQuery
                ? "No outputs match your search query"
                : "This module has no output variables"
            }
            fullHeight
            announce={!!searchQuery}
          />
        )}
        {sortedOutputs.length > 0 && (
          <TableSimple
            data={sortedOutputs}
            store={useModuleVersionOutputsTableStore}
            ariaLabel="Outputs"
            trackAnalytics={trackAnalytics}
            cellPerColumn={MODULE_VERSION_OUTPUTS_TABLE_CELLS}
            filtersStorageKey={MODULE_VERSION_OUTPUTS_TABLE_FILTERS_STORAGE_KEY}
            sortConfig={SORT_CONFIG}
            hasPagination={false}
            hideSettings
            stickyRight={() => false}
          />
        )}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionOutputs;
